import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Flex } from '@abyss/web/ui/Flex';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useContext } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { CountySearchContext } from '../../context/CountySearchContext';
import { useLagoon } from '../../hooks/useLagoon';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { SnackCardProvider } from '../../hooks/useSnackCardProviderSearch/useSnackCardProviderSearch';
import { useSnackcardViewAllCount } from '../../hooks/useSnackcardViewAllCount';
import { useSuppressNiceMembers } from '../../hooks/useSuppressNiceMembers';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { getMaxBadgeCount } from '../../utils/snackcard.utils';
import { getCurrentMember } from '../../utils/user.utils';
import { CareCategories, ProviderTypes } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { phoneOnly } from '../ConstantsStyles';
import { OutsideCountyAlert } from '../DataCard/OutsideCountyAlert';
import { getGeoLocationFromStorage } from '../PSXHeader/SearchBar/utils';
import { CardInfo } from '../SnackCard/CardInfo';
import { SmartChoiceScoreCard } from '../SnackCard/SmartChoiceScoreCard';
import { SnackCard } from '../SnackCard/SnackCard';
import { getFeatureFlag } from '../Utils';
import { isMnrMember } from '../Utils/memberUtils/memberUtils';
import { SnackCardViewAllLink } from './SnackCardViewAllLink';
import { isOutOfCounty } from './utils';

const flexStyles = {
  'abyss-flex-root': {
    gap: '$md',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    overflowY: 'hidden',
    alignItems: 'end',

    '& > div': {
      flex: 'none',
    },
  },
};

type Props = {
  coverageType?: string;
  dataTestId?: string;
  snackCardProviders: SnackCardProvider[];
  isLoading: boolean;
  locationForAnalytics?: string;
  heading?: string;
  rulesPackageKey?: string;
  headers: ResponseHeaders;
  handleViewAllOnClick?: () => void;
  searchRadius?: number;
  isTieredPlan?: boolean;
  isHeadingAcronym?: boolean;
  recommendationPreferences?: any;
};

export const SnackCardContainer = ({
  coverageType,
  snackCardProviders = [],
  dataTestId = '',
  isLoading,
  locationForAnalytics,
  rulesPackageKey,
  headers,
  heading,
  handleViewAllOnClick,
  searchRadius,
  isTieredPlan = false,
  isHeadingAcronym = false,
  recommendationPreferences,
}: Props) => {
  const mobileScreen = useMediaQuery(phoneOnly);

  const featureFlags = useLagoon('feature-flags')();
  const countySearchAlertFlag: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COUNTY_SEARCH_ALERT
  );
  const displayTerminationDateEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_FUTURE_TERMINATION_DATE
  );
  const { isCountySearch } = useContext(CountySearchContext);
  const { name, stateCode } = getGeoLocationFromStorage();
  const { isExpandViewAllEnabled } = useSnackcardViewAllCount();

  const noProvidersFoundData = useLagoon('ui-messaging')();
  const noProvidersFoundDataContent = find(noProvidersFoundData, {
    key: ConstantsLagoon.NO_PROVIDERS_FOUND,
  });
  const getLinkPositionIndex = (linkPosition?: number) => {
    if (linkPosition !== undefined) {
      return linkPosition + 1;
    }
    return linkPosition;
  };

  const linkLocationName = `body:${locationForAnalytics}`;

  const loadingCardArray: number[] = mobileScreen
    ? [1, 2, 3]
    : isExpandViewAllEnabled
    ? [1, 2, 3, 4, 5, 6]
    : [1, 2, 3, 4, 5];
  const { lineOfBusiness } = getCurrentMember();
  const isMNR = isMnrMember(lineOfBusiness);

  // Get chip context for Smart Choice facility logic
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue } = chipStore;

  const hasPhysicalAddress = snackCardProviders.some(
    (snackCardProvider) => !!snackCardProvider.address
  );
  const isVirtualCareOffered = snackCardProviders.some(
    (snackCardProvider) => !!snackCardProvider.virtualCareOffered?.length
  );

  const isOutOfCntyIndicatorEnabled = snackCardProviders?.some(
    (snackCardProvider) =>
      isOutOfCounty(isCountySearch, name, stateCode, snackCardProvider)
  );

  const isPlatinumProviderIdentifierEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLATINUM_PROVIDER_IDENTIFIER
  );

  const suppressPremiumCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );

  const { suppressForNiceMembers: suppressPremiumCareForNiceMembers } =
    useSuppressNiceMembers();

  const isMultipleBadgeCardViewEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MULTIPLE_BADGE_DATA_CARD_VIEW
  );

  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoice = smartChoiceEligibility.isEnabled;
  const maxBadgeCount = getMaxBadgeCount(
    snackCardProviders,
    isPlatinumProviderIdentifierEnabled,
    suppressPremiumCare || suppressPremiumCareForNiceMembers,
    isMultipleBadgeCardViewEnabled,
    displayTerminationDateEnabled
  );

  return (
    <Flex
      css={flexStyles}
      data-auto-testid={dataTestId}
      data-testid={dataTestId}
    >
      {isLoading ? (
        loadingCardArray.map((item) => (
          <SnackCard isLoading={isLoading} key={`${heading}-${item}`} />
        ))
      ) : snackCardProviders.length ? (
        snackCardProviders.map((snackCardProvider, index) => {
          const isOutOfCnty = isOutOfCounty(
            isCountySearch,
            name,
            stateCode,
            snackCardProvider
          );

          return (
            <React.Fragment key={snackCardProvider.locationId}>
              <div
                data-testid={`snack-card-${snackCardProvider?.providerType}`.replace(
                  / /g,
                  '-'
                )}
                style={{
                  display: 'grid',
                  flexDirection: 'column',
                  gridTemplateRows: 'max-content',
                }}
              >
                {isSmartChoice &&
                (snackCardProvider?.providerType?.toLocaleLowerCase() ===
                  ProviderTypes.PRACTITIONER ||
                  (snackCardProvider?.providerType?.toLocaleLowerCase() ===
                    ProviderTypes.ORGANIZATION &&
                    chipValue === CareCategories.FACILITIES)) &&
                parseInt(
                  snackCardProvider?.recommendationDetails
                    ?.totalRecommendationScore || '0'
                ) > 0 ? (
                  <SmartChoiceScoreCard
                    recommendationDetails={
                      snackCardProvider?.recommendationDetails
                    }
                  />
                ) : null}

                {!isSmartChoice && (
                  <CardInfo
                    cardType="snack-card-container"
                    headers={headers}
                    indexForAnalytics={getLinkPositionIndex(index)}
                    landingPage
                    locationForAnalytics={linkLocationName}
                    practitioner={snackCardProvider}
                    rulesPackageKey={rulesPackageKey}
                  />
                )}
                <SnackCard
                  coverageType={coverageType}
                  distance={snackCardProvider.distance}
                  hasPhysicalAddress={!!snackCardProvider.address}
                  headers={headers}
                  imgSrc={snackCardProvider.imageLocation}
                  index={index}
                  isLoading={isLoading}
                  isMNR={isMNR}
                  isSmartChoiceFFEnabled={isSmartChoice}
                  isTieredPlan={isTieredPlan}
                  isTieredProvider={snackCardProvider?.isTieredProvider}
                  isVirtualCareOffered={
                    !!snackCardProvider.virtualCareOffered?.length
                  }
                  key={snackCardProvider.locationId}
                  llmCodes={snackCardProvider?.llmCodes}
                  locationForAnalytics={locationForAnalytics}
                  locationId={snackCardProvider.locationId}
                  maxBadgeCount={maxBadgeCount}
                  networkEndDate={snackCardProvider?.networkEndDate}
                  networkStartDate={snackCardProvider?.networkStartDate}
                  organizationCode={snackCardProvider.organizationCode}
                  organizationType={snackCardProvider.organizationType}
                  professionalDesignation={
                    snackCardProvider.primaryDegrees?.length
                      ? snackCardProvider.primaryDegrees[0]
                      : ''
                  }
                  providerId={snackCardProvider.providerId}
                  providerName={snackCardProvider.providerName}
                  providerTierDesignation={
                    snackCardProvider?.recommendationDetails?.benefits
                      ?.providerTierDesignation
                  }
                  providerType={snackCardProvider.providerType}
                  rating={
                    snackCardProvider.healthGradeRating
                      ? parseFloat(
                          parseFloat(
                            snackCardProvider.healthGradeRating
                          ).toFixed(1)
                        )
                      : undefined
                  }
                  recommendationPreferences={recommendationPreferences}
                  rulesPackageKey={rulesPackageKey}
                  searchRadius={searchRadius}
                  snackCardProvider={snackCardProvider}
                  speciality={snackCardProvider?.speciality}
                  virtualIndicator={snackCardProvider?.virtualIndicator || ''}
                  website={snackCardProvider?.website || ''}
                />
                {(countySearchAlertFlag || isOutOfCntyIndicatorEnabled) && (
                  <OutsideCountyAlert
                    county={name?.split(',')[0]}
                    isLandingPage
                    isOutOfCntyIndicatorEnabled={isOutOfCntyIndicatorEnabled}
                    isOutOfCounty={isOutOfCnty}
                  />
                )}
              </div>
              {isExpandViewAllEnabled &&
                index === snackCardProviders.length - 1 &&
                handleViewAllOnClick && (
                  <SnackCardViewAllLink
                    handleViewAllOnClick={handleViewAllOnClick}
                    hasPhysicalAddress={hasPhysicalAddress}
                    heading={heading}
                    isHeadingAcronym={isHeadingAcronym}
                    isOutOfCntyIndicatorEnabled={isOutOfCntyIndicatorEnabled}
                    isVirtualCareOffered={isVirtualCareOffered}
                    key={snackCardProvider?.providerType}
                    maxBadgeCount={maxBadgeCount}
                    providerType={snackCardProvider.providerType}
                  />
                )}
            </React.Fragment>
          );
        })
      ) : (
        <Text
          color="#6E7072"
          css={{
            'abyss-text-root': {
              fontWeight: 500,
            },
          }}
          data-testid="no-provider-results-message"
        >
          {noProvidersFoundDataContent?.message ?? ''}
        </Text>
      )}
    </Flex>
  );
};
